import { debounce } from '@/utils/simpleDebounce';
import {
  CRUD_TABLE_FIT_DEFAULTS,
  calcItemsPerPageToFit,
  resolveCrudFitAfterApiLoad,
  resolveCrudTableFitMetrics
} from '@/utils/tableUtils';
import { createLatestRequestGuard } from '@/utils/latestRequestWins';

/**
 * 관리자 CRUD 표: 실제 디스플레이(호스트·크롬·행 실측)에 맞춰 itemsPerPage를 맞춘다.
 *
 * 컴포넌트 요구:
 * - data: itemsPerPage (Number)
 * - 선택: tableOptions.itemsPerPage, crudTableFitOptions, page, apiLoading
 * - 선택: ref="crudTableFitRoot" (없으면 $el)
 * - 선택: onCrudPageSizeFitChanged(next) — 없으면 initialize() 호출
 *
 * 흐름:
 * 1) created: fallback 수치로 1차 size (첫 API용)
 * 2) mounted: 호스트/크롬 DOM 실측으로 보정
 * 3) 첫 apiLoading false: ResizeObserver 연결. 이후 삭제·재조회 완료에서는 fit을 다시 돌리지 않음
 * 4) resize / ResizeObserver: 디스플레이·창 크기 변경 반영
 */
export const crudTablePageSizeFitMixin = {
  watch: {
    apiLoading(val, oldVal) {
      if (oldVal === true && val === false) {
        const action = resolveCrudFitAfterApiLoad({
          initialLoadSettled: this._crudFitInitialLoadSettled
        });
        this._crudFitProgrammaticOptionsChange = false;
        if (action.bindObservers) {
          this._onCrudPageSizeFitResize?.cancel?.();
          this._crudFitInitialLoadSettled = true;
          this._bindCrudFitObservers();
        }
        if (action.scheduleFitRefetch) {
          this.scheduleCrudPageSizeFitRefetch();
        }
      }
    }
  },

  created() {
    this._crudFitCalibrating = false;
    this._crudFitDestroyed = false;
    this._crudFitInitialLoadSettled = false;
    this._crudFitReady = false;
    this._crudFitIgnoreNextResize = false;
    this._crudFitPendingOptions = null;
    this._crudFitProgrammaticOptionsChange = false;
    this._crudFitOriginalChangedOptions =
      typeof this.changedOptions === 'function' ? this.changedOptions : null;
    if (this._crudFitOriginalChangedOptions) {
      this.changedOptions = (...args) => {
        if (this._crudFitProgrammaticOptionsChange) {
          return;
        }
        if (!this._crudFitReady) {
          this._crudFitPendingOptions = args;
          return;
        }
        return this._crudFitOriginalChangedOptions.apply(this, args);
      };
    }
    this._crudTableRequestGuard = createLatestRequestGuard();
    this.applyCrudPageSizeFit({ refetch: false });
    const delay =
      this.crudTableFitOptions?.resizeDebounceMs ?? CRUD_TABLE_FIT_DEFAULTS.resizeDebounceMs;
    this._onCrudPageSizeFitResize = debounce(() => {
      if (!this._crudFitDestroyed && this._crudFitInitialLoadSettled) {
        this.applyCrudPageSizeFit({ refetch: true });
      }
    }, delay);
  },

  mounted() {
    this.$nextTick(() => {
      this._markCrudFitProgrammaticOptionsChange();
      this.applyCrudPageSizeFit({ refetch: false });
      this._crudFitReady = true;
      this._flushCrudFitPendingOptions();
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this._onCrudPageSizeFitResize);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this._onCrudPageSizeFitResize);
      }
    }
  },

  beforeDestroy() {
    this._crudFitDestroyed = true;
    this._teardownCrudPageSizeFit();
  },

  beforeUnmount() {
    this._crudFitDestroyed = true;
    this._teardownCrudPageSizeFit();
  },

  methods: {
    _markCrudFitProgrammaticOptionsChange() {
      this._crudFitProgrammaticOptionsChange = true;
    },

    _flushCrudFitPendingOptions() {
      if (!this._crudFitOriginalChangedOptions) {
        return;
      }
      const pendingOptions = this._crudFitPendingOptions;
      this._crudFitPendingOptions = null;
      if (pendingOptions) {
        this._crudFitOriginalChangedOptions.apply(this, pendingOptions);
        return;
      }
      this._crudFitOriginalChangedOptions.call(this, {
        sortBy: this.tableOptions?.sortBy,
        sortDesc: this.tableOptions?.sortDesc
      });
    },

    isCrudTableFitProgrammaticUpdate() {
      return this._crudFitProgrammaticOptionsChange;
    },

    beginCrudTableRequest() {
      // 삭제·재조회로 표 높이가 바뀌어도 ResizeObserver가 fit을 다시 돌리지 않게 한다.
      this._crudFitIgnoreNextResize = true;
      return this._crudTableRequestGuard.begin();
    },

    isCrudTableRequestCurrent(token) {
      return this._crudTableRequestGuard.isCurrent(token);
    },

    finishCrudTableRequest(token) {
      this._crudTableRequestGuard.finish(token);
    },

    cancelCrudTableRequest() {
      this._crudTableRequestGuard.cancel();
      this.onCrudTableRequestCancel?.();
    },

    _getCrudFitRootEl() {
      const ref = this.$refs?.crudTableFitRoot;
      if (ref) {
        return ref.$el || ref;
      }
      return this.$el || null;
    },

    _bindCrudFitObservers() {
      this._teardownCrudFitObserversOnly();
      const root = this._getCrudFitRootEl();
      const host =
        (root && typeof root.closest === 'function' && root.closest('.admin-crud-fit-host')) ||
        (typeof document !== 'undefined' && document.querySelector('.admin-crud-fit-host'));

      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      this._crudFitResizeObserver = new ResizeObserver(() => {
        if (this._crudFitIgnoreNextResize) {
          this._crudFitIgnoreNextResize = false;
          return;
        }
        if (this._onCrudPageSizeFitResize) {
          this._onCrudPageSizeFitResize();
        }
      });
      if (host) {
        this._crudFitIgnoreNextResize = true;
        this._crudFitResizeObserver.observe(host);
      } else if (root) {
        this._crudFitIgnoreNextResize = true;
        this._crudFitResizeObserver.observe(root);
      }
    },

    _teardownCrudFitObserversOnly() {
      if (this._crudFitResizeObserver) {
        this._crudFitResizeObserver.disconnect();
        this._crudFitResizeObserver = null;
      }
    },

    _teardownCrudPageSizeFit() {
      this._teardownCrudFitObserversOnly();
      if (typeof window !== 'undefined' && this._onCrudPageSizeFitResize) {
        this._onCrudPageSizeFitResize.cancel?.();
        window.removeEventListener('resize', this._onCrudPageSizeFitResize);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this._onCrudPageSizeFitResize);
        }
      }
      this.cancelCrudTableRequest?.();
    },

    scheduleCrudPageSizeFitRefetch() {
      this._onCrudPageSizeFitResize?.();
    },

    /**
     * 데이터 행이 그려진 뒤 행 높이까지 실측해 보정한다.
     * @param {{ refetch?: boolean }} [options]
     */
    calibrateCrudPageSizeFromDom({ refetch = true } = {}) {
      if (this._crudFitCalibrating) {
        return this.itemsPerPage;
      }
      return this.applyCrudPageSizeFit({ refetch, preferMeasuredRow: true });
    },

    /**
     * @param {{ refetch?: boolean, preferMeasuredRow?: boolean }} [options]
     * @returns {number} 적용된 itemsPerPage
     */
    applyCrudPageSizeFit({ refetch = false, preferMeasuredRow = false } = {}) {
      if (this._crudFitDestroyed) {
        return this.itemsPerPage;
      }
      const fitOpts = this.crudTableFitOptions || {};
      const root = this._getCrudFitRootEl();
      const metrics = resolveCrudTableFitMetrics(root, {
        rowHeightFallback: fitOpts.rowHeight || CRUD_TABLE_FIT_DEFAULTS.rowHeight,
        chromeHeightFallback: fitOpts.chromeHeight || CRUD_TABLE_FIT_DEFAULTS.chromeHeight
      });

      // 첫 요청 전에는 행이 없어 fallback rowHeight를 씀.
      // preferMeasuredRow가 아니면, 실측 행이 fallback과 크게 달라도
      // (아직 이미지 로딩 전 등) fitOpts.rowHeight가 있으면 그걸 우선할 수 있음.
      let rowHeight = metrics.rowHeight;
      if (!preferMeasuredRow && fitOpts.rowHeight && measureHasNoDataRows(root)) {
        rowHeight = fitOpts.rowHeight;
      }

      const next = calcItemsPerPageToFit({
        availableHeight: metrics.availableHeight,
        chromeHeight: metrics.chromeHeight,
        rowHeight,
        min: fitOpts.min,
        max: fitOpts.max
      });

      if (next === this.itemsPerPage) {
        return next;
      }

      if (refetch) {
        this._markCrudFitProgrammaticOptionsChange();
      }
      this.itemsPerPage = next;
      if (this.tableOptions && typeof this.tableOptions === 'object') {
        this.$set
          ? this.$set(this.tableOptions, 'itemsPerPage', next)
          : (this.tableOptions.itemsPerPage = next);
      }

      if (refetch) {
        this._crudFitCalibrating = true;
        if (typeof this.page === 'number') {
          this.page = 1;
        }
        if (this.tableOptions && typeof this.tableOptions === 'object') {
          this.$set
            ? this.$set(this.tableOptions, 'page', 1)
            : (this.tableOptions.page = 1);
        }
        try {
          if (typeof this.onCrudPageSizeFitChanged === 'function') {
            this.onCrudPageSizeFitChanged(next);
          } else if (typeof this.initialize === 'function') {
            this.initialize();
          }
        } finally {
          this.$nextTick(() => {
            this._crudFitCalibrating = false;
          });
        }
      }

      return next;
    }
  }
};

function measureHasNoDataRows(rootEl) {
  if (!rootEl || typeof rootEl.querySelectorAll !== 'function') {
    return true;
  }
  const rows = rootEl.querySelectorAll(
    '.v-data-table tbody tr:not(.v-data-table__progress):not(.v-data-table__empty-wrapper)'
  );
  return rows.length === 0;
}
